// deps
import React, { memo, useCallback, ReactElement, useMemo } from "react";

// components
import { AmountInputBox, AmountRadioWrapper } from "@swiggy-private/core-ui";
import ViolationMessage from "./components/ViolationMessage";

// interfaces
import { OnAmountBoxSelectionParams, AmountSelectionWidgetProps } from "@payment-ui/interfaces/SwiggyInstruments";

// test-ids
import { ADD_SWIGGY_MONEY_TEST_IDS } from "@swiggy-private/payment-test-ids";

const AmountSelectionRadioWidget = ({
    amountOptions,
    amount,
    reflectChangedAmountImmediately,
    onUpdateAmount,
    inputScrollIntoView,
    clickPredefinedAmount,
    inputFieldPlaceHolder,
    swiggyMoneyInfoHalfCard,
    swiggyMoneyInfoActions,
    violationInfo,
}: AmountSelectionWidgetProps): ReactElement => {
    const updateAmount = useCallback(
        (updatedAmount?: string) => {
            const otherAmount = Number(updatedAmount);
            const amount = isNaN(otherAmount) ? 0 : otherAmount;
            onUpdateAmount(amount);
        },
        [onUpdateAmount],
    );

    const onPredefinedAmountBoxSelection = useCallback(
        ({ selected, amount }: OnAmountBoxSelectionParams) => {
            if (selected) {
                updateAmount(String(amount));
                clickPredefinedAmount(amount);
            } else {
                updateAmount(String(""));
            }
        },
        [updateAmount, clickPredefinedAmount],
    );

    const amountInputBox = useMemo(() => {
        return (
            <AmountInputBox
                dataTestId={ADD_SWIGGY_MONEY_TEST_IDS.AMOUNT_SELECTION_INPUT_BOX}
                amount={String(amount)}
                onUpdateAmount={updateAmount}
                reflectChangedAmountImmediately={reflectChangedAmountImmediately}
                scrollIntoView={inputScrollIntoView}
                inputFieldPlaceHolder={inputFieldPlaceHolder}
            />
        );
    }, [amount, updateAmount, reflectChangedAmountImmediately, inputScrollIntoView, inputFieldPlaceHolder]);

    const suggestionBoxDataTestIds = useMemo(() => {
        return {
            default: ADD_SWIGGY_MONEY_TEST_IDS.AMOUNT_SUGGESION_BOX,
            selected: ADD_SWIGGY_MONEY_TEST_IDS.AMOUNT_SUGGESION_BOX_SELECTED,
        };
    }, []);

    const amountRadioWrapper = useMemo(() => {
        return (
            <AmountRadioWrapper
                data-testid={ADD_SWIGGY_MONEY_TEST_IDS.AMOUNT_SELECTION_CONTAINER}
                suggestionBoxDataTestIds={suggestionBoxDataTestIds}
                onPredefinedAmountBoxSelection={onPredefinedAmountBoxSelection}
                selectedAmount={amount}
                amountOptions={amountOptions}
            />
        );
    }, [suggestionBoxDataTestIds, onPredefinedAmountBoxSelection, amount, amountOptions]);

    return (
        <>
            {amountInputBox}
            <ViolationMessage
                violation={violationInfo}
                actions={swiggyMoneyInfoActions}
                swiggyMoneyInfoHalfCard={swiggyMoneyInfoHalfCard}
            />
            {amountRadioWrapper}
        </>
    );
};

export default memo(AmountSelectionRadioWidget);
